import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Banner, Button, KeyValueGrid, MoneyValue, Skeleton } from '@shared/design-system/ui'
import { formatMoney } from '@shared/lib/format/currency'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  advanceSubscriptionTest,
  useSubscriptionTestContext,
  type SubscriptionTestInvoice,
  type SubscriptionTestResult,
} from '../api/admin.api'
import { saleLineHint, saleSection, saleSectionTitle, saleSoftNote } from './admin.css'

type AdminSubscriptionTestPanelProps = {
  subscriptionId: string
  active: boolean
}

export function AdminSubscriptionTestPanel({
  subscriptionId,
  active,
}: AdminSubscriptionTestPanelProps) {
  const [lastResult, setLastResult] = useState<SubscriptionTestResult | null>(null)
  const queryClient = useQueryClient()
  const context = useSubscriptionTestContext(subscriptionId, active)
  const advance = useMutation({
    mutationFn: (invoiceId: string) => advanceSubscriptionTest(subscriptionId, invoiceId),
    onSuccess: (result) => {
      setLastResult(result)
      toast.success(
        result.outcome === 'SUCCEEDED'
          ? 'Ciclo processado com sucesso.'
          : 'Processamento do ciclo concluído.',
      )
      void queryClient.invalidateQueries({ queryKey: ['admin-subscription-test', subscriptionId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-subscriptions-infinite'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível processar o ciclo',
          fallback: 'Confira a assinatura e tente novamente.',
        }),
      )
    },
  })

  function confirmAdvance(invoice: SubscriptionTestInvoice) {
    const amount = formatMoney(invoice.chargeAmount.amount, invoice.chargeAmount.currency)
    const confirmed = window.confirm(
      `Processar agora o ciclo ${invoice.cycleN} no valor de ${amount}?\n\nIsso criará uma cobrança Stripe de teste e executará o fluxo normal de renovação.`,
    )
    if (confirmed) {
      advance.mutate(invoice.id)
    }
  }

  if (context.isPending) {
    return <Skeleton height={180} />
  }
  if (context.isError) {
    const errorMessage = getUserFacingError(context.error, {
      title: 'Não foi possível preparar o teste',
      fallback: 'Tente novamente em alguns instantes.',
    })
    return (
      <Banner tone="danger" title="Não foi possível preparar o teste">
        {errorMessage.description}
      </Banner>
    )
  }
  if (!context.data) return null

  const invoice = context.data.nextInvoice

  return (
    <div className={saleSection}>
      <span className={saleSectionTitle}>Processar próximo ciclo</span>
      <Banner tone="warning" title="Ação exclusiva de QA">
        A cobrança usa a conta Stripe de teste da assinatura e executa o fluxo normal de renovação,
        incluindo venda, eventos e agendamento do ciclo seguinte.
      </Banner>

      {invoice ? (
        <>
          <KeyValueGrid
            items={[
              { label: 'Invoice', value: invoice.id },
              { label: 'Ciclo', value: String(invoice.cycleN) },
              { label: 'Vencimento original', value: formatDateTime(invoice.dueAt) },
              {
                label: 'Cobrança',
                value: (
                  <MoneyValue
                    amount={invoice.chargeAmount.amount}
                    currency={invoice.chargeAmount.currency}
                  />
                ),
              },
            ]}
          />
          <Button
            type="button"
            disabled={!context.data.eligibility.canAdvance || advance.isPending}
            onClick={() => confirmAdvance(invoice)}
          >
            {advance.isPending ? 'Processando ciclo...' : 'Processar próximo ciclo agora'}
          </Button>
          {!context.data.eligibility.canAdvance ? (
            <span className={saleLineHint}>{context.data.eligibility.reason}</span>
          ) : null}
        </>
      ) : (
        <div className={saleSoftNote}>
          {context.data.eligibility.reason ?? 'Não há uma invoice pendente para processar.'}
        </div>
      )}

      {lastResult ? (
        <div className={saleSoftNote} role="status">
          Resultado: <strong>{outcomeLabel(lastResult.outcome)}</strong>. Invoice{' '}
          {lastResult.processedInvoice.id} ficou como {lastResult.processedInvoice.status}.
          {lastResult.nextInvoice
            ? ` Próximo ciclo: ${lastResult.nextInvoice.cycleN}, previsto para ${formatDateTime(lastResult.nextInvoice.dueAt)}.`
            : ''}
        </div>
      ) : null}
    </div>
  )
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function outcomeLabel(value: SubscriptionTestResult['outcome']) {
  const labels: Record<SubscriptionTestResult['outcome'], string> = {
    SUCCEEDED: 'sucesso',
    FAILED: 'falha de pagamento',
    REQUIRES_ACTION: 'autenticação necessária',
    BLOCKED: 'bloqueado',
    AMBIGUOUS: 'resultado pendente',
    ALREADY_PROCESSED: 'já processado',
    SKIPPED: 'não processado',
  }
  return labels[value]
}
